import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@api/useAuth";
import { useDailyBankBalances, useBankAccounts } from "@api/hooks";
import { isFinanceCoordinator } from "@utils";
import AlertModal from "./AlertModal";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const TODAY = todayISO();

const TODAY_LABEL = new Date().toLocaleDateString("en-CA", {
  month: "long",
  day: "numeric",
  year: "numeric",
});

export default function DailyBankBalanceAlert() {
  const { user, isLoading: authLoading } = useAuth();
  // Only Finance Coordinator gets the missing-entry nudge — daily bank balance
  // entry is their responsibility. (Other roles can still write balances via
  // the Cash Flow → Daily Bank Balance tab; they just aren't reminded.)
  const isEligible = !authLoading && !!user && isFinanceCoordinator(user);
  const [open, setOpen] = useState(false);

  // Count the accounts a user can actually enter on the Daily Bank Balance tab —
  // the filter here MUST match that page's (`status=active`, `has_clinic=true`),
  // or this nudges about entity-level accounts that page doesn't show, and the
  // count can never reach zero.
  const accountsQuery = useBankAccounts(
    { status: "active", has_clinic: "true", limit: 500 },
    { enabled: isEligible },
  );

  const accountCount = useMemo(() => {
    const rows = accountsQuery.data?.results ?? accountsQuery.data ?? [];
    return rows.length;
  }, [accountsQuery.data]);

  // Fetch only today's entries — much cheaper than loading the full history.
  const balancesQuery = useDailyBankBalances({ date: TODAY }, { enabled: isEligible });

  const enteredToday = useMemo(() => {
    const entries = balancesQuery.data?.results ?? balancesQuery.data ?? [];
    // Count only entries on clinic-owned accounts, to stay on the same footing as
    // `accountCount` above. An entity-level entry would otherwise shrink the
    // awaiting count against a total that never included it.
    return entries.filter((e) => e.clinic_code).length;
  }, [balancesQuery.data]);

  const dataReady = accountsQuery.isSuccess && balancesQuery.isSuccess;
  const awaitingCount = dataReady ? Math.max(0, accountCount - enteredToday) : 0;

  useEffect(() => {
    if (dataReady && awaitingCount > 0) setOpen(true);
  }, [dataReady, awaitingCount]);

  if (!isEligible || !open) return null;

  return (
    <AlertModal
      isOpen={open}
      onClose={() => setOpen(false)}
      title="Daily Bank Balance — Missing Entries"
    >
      <p className="text-smiles-darkGray">
        <span className="font-semibold text-palette-yellow-secondary">{awaitingCount}</span> bank
        account{awaitingCount !== 1 ? "s are" : " is"} still awaiting a balance entry for{" "}
        <span className="font-medium">{TODAY_LABEL}</span>. Please visit{" "}
        <span className="font-medium">Cash Flow → Daily Bank Balance</span> to enter the missing
        balance{awaitingCount !== 1 ? "s" : ""}.
      </p>
    </AlertModal>
  );
}
